import { apiClient } from './client';
import type {
  Asset,
  AssetAssignmentHistoryItem,
  AssetDetail,
  AssetQuery,
  BulkUpdateAssetsResult,
  BulkUpdateAssets,
  BulkCreateAssets,
  LookupItem,
  PagedResult,
  UpsertAsset,
} from '@/types/asset';

/**
 * Convert an {@link AssetQuery} filter object into URL search params for the assets list endpoint.
 * @param query - Filter/pagination/sort parameters.
 * @returns Populated URLSearchParams containing only the fields that were set.
 */
function toParams(query: AssetQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) params.set('search', query.search);
  if (query.category) params.set('category', query.category);
  if (query.status) params.set('status', query.status);
  query.statuses?.forEach((status) => params.append('statuses', status));
  if (query.manufacturer) params.set('manufacturer', query.manufacturer);
  if (query.purchaseType) params.set('purchaseType', query.purchaseType);
  if (query.keyboardLayout) params.set('keyboardLayout', query.keyboardLayout);
  if (query.leaseExpiring) params.set('leaseExpiring', String(query.leaseExpiring));
  if (query.leaseExpiringMonths) params.set('leaseExpiringMonths', String(query.leaseExpiringMonths));
  if (query.assignedEmployeeId) params.set('assignedEmployeeId', String(query.assignedEmployeeId));
  if (query.officeLocationId) params.set('officeLocationId', String(query.officeLocationId));
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDescending) params.set('sortDescending', String(query.sortDescending));
  return params;
}

/** API client for asset CRUD, bulk operations, and lookup endpoints. */
export const assetsApi = {
  /**
   * List assets matching the given filters, paginated.
   * @param query - Filter/pagination/sort parameters.
   * @returns A page of matching assets.
   */
  async list(query: AssetQuery): Promise<PagedResult<Asset>> {
    const { data } = await apiClient.get<PagedResult<Asset>>('/api/assets', {
      params: toParams(query),
    });
    return data;
  },

  /**
   * Fetch full details for a single asset.
   * @param id - Asset id.
   * @returns The asset detail.
   */
  async get(id: number): Promise<AssetDetail> {
    const { data } = await apiClient.get<AssetDetail>(`/api/assets/${id}`);
    return data;
  },

  /**
   * Fetch the assignment (employee/location) history for a single asset.
   * @param id - Asset id.
   * @returns Assignment history entries, ordered as returned by the API.
   */
  async getAssignmentHistory(id: number): Promise<AssetAssignmentHistoryItem[]> {
    const { data } = await apiClient.get<AssetAssignmentHistoryItem[]>(`/api/assets/${id}/assignment-history`);
    return data;
  },

  /**
   * Create a new asset.
   * @param payload - Asset fields to create.
   * @returns The created asset's details.
   */
  async create(payload: UpsertAsset): Promise<AssetDetail> {
    const { data } = await apiClient.post<AssetDetail>('/api/assets', payload);
    return data;
  },

  /**
   * Update an existing asset.
   * @param id - Asset id.
   * @param payload - Full replacement of the asset's editable fields.
   * @returns The updated asset's details.
   */
  async update(id: number, payload: UpsertAsset): Promise<AssetDetail> {
    const { data } = await apiClient.put<AssetDetail>(`/api/assets/${id}`, payload);
    return data;
  },

  /**
   * Create multiple assets in a single request.
   * @param payload - Batch of assets to create.
   * @returns The created assets' details.
   */
  async bulkCreate(payload: BulkCreateAssets): Promise<AssetDetail[]> {
    const { data } = await apiClient.post<AssetDetail[]>('/api/assets/bulk', payload);
    return data;
  },

  /**
   * Apply a partial update to multiple assets in a single request.
   * @param payload - Ids plus the fields to patch.
   * @returns A summary of per-asset success/failure results.
   */
  async bulkUpdate(payload: BulkUpdateAssets): Promise<BulkUpdateAssetsResult> {
    const { data } = await apiClient.patch<BulkUpdateAssetsResult>('/api/assets/bulk', payload);
    return data;
  },

  /**
   * Delete an asset.
   * @param id - Asset id.
   */
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/api/assets/${id}`);
  },

  /**
   * Look up employees for asset-assignment autocomplete.
   * @param search - Optional search text.
   * @returns Matching employee lookup items.
   */
  async employeeLookup(search?: string): Promise<LookupItem[]> {
    const { data } = await apiClient.get<LookupItem[]>('/api/Assets/lookups/employees', {
      params: search ? { search } : undefined,
    });
    return data;
  },

  /**
   * Look up office locations for asset-assignment autocomplete.
   * @param search - Optional search text.
   * @returns Matching office location lookup items.
   */
  async officeLocationLookup(search?: string): Promise<LookupItem[]> {
    const { data } = await apiClient.get<LookupItem[]>('/api/Assets/lookups/locations', {
      params: search ? { search } : undefined,
    });
    return data;
  },

};
